/**
 * Co-Op Combat Renderer
 *
 * Extends CombatGridRenderer with co-op-specific team color system
 * (gold/copper/purple) and obstacle rendering for adventure encounters.
 */

import * as PIXI from 'pixi.js';
import { CombatGridRenderer } from '../CombatGrid/CombatGridRenderer';
import type { AxialCoordinate } from '@drawn-of-war/shared';

// Co-op team colors
export const COOP_TEAM_COLORS = {
  mine: 0xFFD700,       // Gold -- current user's creatures
  partner: 0xB87333,    // Copper -- partner's creatures
  enemy: 0x6B21A8,      // Deep purple -- AI enemies
} as const;

export interface CoOpCombatRendererConfig {
  canvasWidth: number;
  canvasHeight: number;
  battlefieldWidth: number;
  battlefieldHeight: number;
  currentUserId: string;
  obstacles?: AxialCoordinate[];
  showCoordinates?: boolean;
}

export class CoOpCombatRenderer extends CombatGridRenderer {
  private currentUserId: string;
  private obstacles: Set<string>;
  private obstacleContainer: PIXI.Container;
  private teamColorOverrides: Map<string, number> = new Map(); // unitId -> color

  constructor(config: CoOpCombatRendererConfig) {
    super({
      canvasWidth: config.canvasWidth,
      canvasHeight: config.canvasHeight,
      hexGridConfig: {
        width: config.battlefieldWidth,
        height: config.battlefieldHeight,
        hexSize: 40,
        orientation: 'flat-top',
      },
      showCoordinates: config.showCoordinates ?? false,
    });

    this.currentUserId = config.currentUserId;
    this.obstacles = new Set(
      (config.obstacles || []).map(o => `${o.q},${o.r}`)
    );
    this.obstacleContainer = new PIXI.Container();
  }

  /**
   * Initialize renderer, then add obstacle layer.
   */
  override async init(): Promise<HTMLCanvasElement> {
    const canvas = await super.init();

    // Add obstacle container between grid and creature layers
    const worldContainer = this.getWorldContainer();
    if (worldContainer) {
      // Insert obstacle container at index 2 (after background and grid)
      const creatureLayerIndex = worldContainer.children.length - 1;
      worldContainer.addChildAt(this.obstacleContainer, Math.min(2, creatureLayerIndex));
    }

    return canvas;
  }

  /**
   * Render obstacle hexes as semi-transparent dark fills.
   */
  renderObstacles(obstacles: AxialCoordinate[]): void {
    this.obstacleContainer.removeChildren();

    // Update internal obstacle set
    this.obstacles.clear();
    for (const obstacle of obstacles) {
      this.obstacles.add(`${obstacle.q},${obstacle.r}`);
    }

    const hexSize = this.getHexGrid().hexSize;

    for (const obstacle of obstacles) {
      const pixel = this.hexToPixel(obstacle);
      if (!pixel) continue;

      const graphics = new PIXI.Graphics();
      // Semi-transparent dark fill for obstacle
      // Calculate hex vertices (flat-top orientation)
      const points: number[] = [];
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i;
        points.push(
          pixel.x + hexSize * Math.cos(angle),
          pixel.y + hexSize * Math.sin(angle)
        );
      }

      graphics.poly(points);
      graphics.fill({ color: 0x2D1B00, alpha: 0.6 });

      this.obstacleContainer.addChild(graphics);
    }
  }

  /**
   * Get the co-op team color for a creature based on ownership.
   */
  getTeamColor(teamId: string, ownerId: string): number {
    if (teamId === 'team2') {
      return COOP_TEAM_COLORS.enemy;
    }
    if (ownerId === this.currentUserId) {
      return COOP_TEAM_COLORS.mine;
    }
    return COOP_TEAM_COLORS.partner;
  }

  /**
   * Set the team color override for a specific unit.
   * Call this before or after rendering a creature to override the default team color.
   */
  setTeamColorOverride(unitId: string, teamId: string, ownerId: string): void {
    this.teamColorOverrides.set(unitId, this.getTeamColor(teamId, ownerId));
  }

  /**
   * Get the stored team color override for a unit.
   * Returns undefined if no override is set.
   */
  getTeamColorOverride(unitId: string): number | undefined {
    return this.teamColorOverrides.get(unitId);
  }

  /**
   * Apply the team color override to a unit's ground indicator.
   * Should be called after renderCreatureWithId to update the ground ellipse color.
   */
  applyTeamColorOverride(unitId: string): void {
    const color = this.teamColorOverrides.get(unitId);
    if (color === undefined) return;

    const sprite = this.getSpriteByUnitId(unitId);
    if (!sprite) return;

    // Find and update the ground indicator (first Graphics child in the container)
    for (const child of sprite.container.children) {
      if (child instanceof PIXI.Graphics) {
        // Re-draw the ground ellipse with the correct team color
        child.clear();
        child.ellipse(0, sprite.container.height * 0.4, 20, 8);
        child.fill({ color, alpha: 0.3 });
        break;
      }
    }
  }

  /**
   * Check if a hex position is an obstacle.
   */
  isObstacle(hex: AxialCoordinate): boolean {
    return this.obstacles.has(`${hex.q},${hex.r}`);
  }

  /**
   * Clean up resources.
   */
  override destroy(): void {
    this.obstacleContainer.removeChildren();
    this.teamColorOverrides.clear();
    super.destroy();
  }
}
